import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, FormControlLabel, Radio, RadioGroup, Stack, TextField } from "@mui/material";
import { HandlerAutocompleteField } from "./HandlerAutocompleteField";
import { getAllInvestmentHouseCodes, InvestmentHouseCode } from "../../utils/investmentHouseCodeFormatter";
import { getHandlerCodeList } from "../../utils/namesFormater";

/** the producers to choose from: the Mislaka's (MimshakData) or the investment houses (ManualMimshakData) */
export type ProducerSource = "mislaka" | "investment_house";

const INVESTMENT_HOUSES = getAllInvestmentHouseCodes();

export const isInvestmentHouse = (code: string | null | undefined) =>
  !!code && Object.values(InvestmentHouseCode).includes(code as InvestmentHouseCode);

const sourceOf = (code: string | null | undefined): ProducerSource => (isInvestmentHouse(code) ? "investment_house" : "mislaka");

interface Props {
  value: string;
  onChange: (handlerCode: string) => void;
  /** the list shown - given: controlled by the parent, else kept here (from the value) */
  source?: ProducerSource;
  /** the list changed - the parent clears the value (without it: cleared here, onChange("")) */
  onSourceChange?: (source: ProducerSource) => void;
  placeholder?: string;
  investmentHelperText?: string;
  size?: "small" | "medium";
  showIcons?: boolean;
}

/**
 * A producer: a Mislaka producer (the handler list, as HandlerAutocompleteField) or an investment house
 * (InvestmentHouseCode - picked by its code: some share a name with a Mislaka producer, e.g. הפניקס)
 */
export const ProducerAutocompleteField: React.FC<Props> = ({
  value, onChange, source: controlledSource, onSourceChange, placeholder, investmentHelperText, size, showIcons,
}) => {
  const [ownSource, setOwnSource] = useState<ProducerSource>(sourceOf(value));
  // another value came in (another row / template) - its list
  useEffect(() => {
    if (value) setOwnSource(sourceOf(value));
  }, [value]);
  const source = controlledSource ?? ownSource;

  return (
    <Stack spacing={1} sx={{ width: "100%" }}>
      <RadioGroup
        row value={source}
        onChange={(e) => {
          const next = e.target.value as ProducerSource;
          setOwnSource(next);
          if (onSourceChange) onSourceChange(next);
          else onChange("");
        }}
      >
        <FormControlLabel value="mislaka" control={<Radio size="small" />} label="יצרני מסלקה" />
        <FormControlLabel value="investment_house" control={<Radio size="small" />} label="בתי השקעות" />
      </RadioGroup>
      {source === "mislaka" ? (
        <HandlerAutocompleteField value={value} placeholder={placeholder} showIcons={showIcons} onChange={onChange} />
      ) : (
        <Autocomplete
          size={size}
          slotProps={{ popper: { dir: "rtl" } }}
          options={INVESTMENT_HOUSES}
          value={INVESTMENT_HOUSES.find((h) => h.value === value) ?? null}
          getOptionLabel={(h) => h.label}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          onChange={(_, h) => onChange(h?.value ?? "")}
          renderInput={(params) => <TextField {...params} label="בית השקעות" helperText={investmentHelperText} />}
        />
      )}
    </Stack>
  );
};

interface ProducerOption {
  code: string;
  name: string;
  group: string;
}

/**
 * The same choice in one line (a grid cell): one list, grouped - the Mislaka producers, then the investment houses.
 * Picked by code (names repeat between the groups).
 */
export const GroupedProducerAutocompleteField: React.FC<{ value: string; onChange: (handlerCode: string) => void }> = ({
  value, onChange,
}) => {
  const options = useMemo<ProducerOption[]>(() => [
    ...getHandlerCodeList().map((h) => ({ code: h.code, name: h.name, group: "יצרני מסלקה" })),
    ...INVESTMENT_HOUSES.map((h) => ({ code: h.value, name: h.label, group: "בתי השקעות" })),
  ], []);
  return (
    <Autocomplete
      size="small" fullWidth
      slotProps={{ popper: { dir: "rtl", sx: { minWidth: 260 } } }}
      options={options}
      groupBy={(o) => o.group}
      value={options.find((o) => o.code === value) ?? null}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      onChange={(_, o) => onChange(o?.code ?? "")}
      renderInput={(params) => <TextField {...params} placeholder="בחר יצרן..." />}
    />
  );
};
